import { useEffect, useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { AgentMode } from '@cypremos/shared'
import type { ChatBlock } from './agent/types'

export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  insufficientEvidence?: boolean
  /** Agent-chat attachments (wizard actions, confirm gate, history cards). Rendered by the host via renderBlocks. */
  blocks?: ChatBlock[]
  /** Which backend produced an agent-chat reply. */
  mode?: AgentMode
}

export interface ChatPanelProps {
  messages: Message[]
  inputValue: string
  onInputChange: (value: string) => void
  onSend: () => void
  loading: boolean
  /** Rendered below the latest assistant message, e.g. a workflow-launch CTA. */
  ctaSlot?: React.ReactNode
  /**
   * Hides the "Pro" mode badge in the input bar. The badge's default visibility is
   * gated by the `sm:` *viewport* breakpoint, which fires based on window width, not
   * this panel's actual host width — so narrow hosts (WorkflowSidebar, the floating
   * chat drawer) still show it at typical desktop window sizes and the badge squeezes
   * the textarea until its placeholder wraps. Hosts narrower than ~500px should pass
   * this explicitly rather than relying on the viewport check.
   */
  compact?: boolean
  /** Renders a message's `blocks` below its text (host-owned, so ChatPanel stays presentation-only). */
  renderBlocks?: (message: Message) => React.ReactNode
  /** Replaces the default "Analyzing documents..." thinking label. */
  loadingLabel?: string
  /** Shown under the empty-state greeting: what the Chat can do on this page. */
  emptyHint?: string
  /** Example prompts under the empty-state greeting; clicking one puts it in the input. */
  suggestions?: string[]
}

/**
 * The chat conversation body — empty-state greeting, message canvas, and input bar.
 * Contains no header and no full-bleed / viewport-height sizing: it fills its host
 * container (h-full) so both ChatPage (full-screen, with header) and the Sidebar
 * (narrow panel, no header) can embed it. Hosts own their own header + sizing/bleed.
 *
 * Externally controlled: the host owns messages/input/loading state so the identical
 * component can be driven by main-Chat state in one mount and Sidebar workflow-chat
 * state in another, without one instance owning the other's data.
 */
export function ChatPanel({ messages, inputValue, onInputChange, onSend, loading, ctaSlot, compact = false, renderBlocks, loadingLabel, emptyHint, suggestions }: ChatPanelProps) {
  const { t } = useTranslation()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLElement>(null)
  const inputAreaRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Grow the textarea with its content up to its max-height, then scroll inside it,
  // so a pasted multi-line requirement stays readable instead of hiding behind one row.
  useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight}px`
    textarea.style.overflowY = textarea.scrollHeight > textarea.clientHeight ? 'auto' : 'hidden'
  }, [inputValue])

  // The input bar floats over the canvas, so the canvas keeps bottom padding equal to the
  // bar's live height; otherwise a growing textarea covers the latest messages.
  useEffect(() => {
    const canvas = canvasRef.current
    const inputArea = inputAreaRef.current
    if (!canvas || !inputArea || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      const atBottom = canvas.scrollHeight - canvas.scrollTop - canvas.clientHeight < 24
      canvas.style.paddingBottom = `${inputArea.offsetHeight + 16}px`
      if (atBottom) canvas.scrollTop = canvas.scrollHeight
    })
    observer.observe(inputArea)
    return () => observer.disconnect()
  }, [])

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onSend()
    }
  }

  const isEmpty = messages.length === 0
  // The full-page chat lifts the input to mid-screen while empty; the narrow drawer keeps it
  // docked at the bottom, where a chat input is expected.
  const floatInput = isEmpty && !compact
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : undefined

  return (
    <div className="flex-1 flex flex-col relative min-h-0 h-full overflow-hidden">
      {/* Chat History Canvas */}
      <main ref={canvasRef} className="flex-1 overflow-y-auto px-margin-mobile md:px-gutter py-8 pb-40 flex flex-col items-center">
        {isEmpty ? (
          <div className="flex-1 flex flex-col items-center justify-center w-full max-w-chat-width mb-32">
            <h1 className={`${compact ? 'text-3xl' : 'text-4xl md:text-5xl'} font-medium text-fg opacity-90 text-center tracking-tight`} style={{ background: 'linear-gradient(90deg, #b8c1ec, #fffffe)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {t('chat.greeting', 'What can I help with?')}
            </h1>
            {emptyHint && <p className="mt-4 text-sm text-fg-muted text-center max-w-md">{emptyHint}</p>}
            {suggestions && suggestions.length > 0 && (
              <div className="mt-4 flex flex-col gap-2 w-full max-w-md">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => {
                      onInputChange(suggestion)
                      textareaRef.current?.focus()
                    }}
                    className="text-left text-sm text-fg bg-bg-surface-2/60 border border-line rounded-xl px-3 py-2 hover:border-brand/50 transition-colors whitespace-pre-line"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="w-full max-w-chat-width flex flex-col gap-stack-lg">
            {messages.map((message) => (
              <div key={message.id} className={`flex flex-col ${message.role === 'user' ? 'items-end mt-4' : 'items-start gap-2'} w-full`}>
                {(message.role === 'user' || message.content || message.insufficientEvidence) && (
                <div
                  className={`${
                    message.role === 'user'
                      ? 'bg-bg-surface-2 text-fg rounded-3xl rounded-br-sm border border-line shadow-sm'
                      : 'text-fg'
                  } font-body-md text-body-md ${message.role === 'user' ? 'px-5 py-3.5' : 'py-2'} max-w-[85%]`}
                >
                  {message.role === 'assistant' ? (
                    message.insufficientEvidence ? (
                      <div className="flex items-center gap-2 text-fg-muted italic">
                        <span className="material-symbols-outlined text-[18px]">info</span>
                        {t('chat.insufficientEvidence', 'I don\'t have documentation that answers this. Try rephrasing, or check the relevant feature page directly.')}
                      </div>
                    ) : (
                      <div
                        className="[&_h1]:text-xl [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-2
                          [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2
                          [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1.5
                          [&_p]:mb-3 [&_p]:leading-relaxed
                          [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3
                          [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
                          [&_li]:mb-1
                          [&_code]:bg-bg-surface-2 [&_code]:px-1 [&_code]:rounded [&_code]:text-sm
                          [&_pre]:bg-bg-surface-2 [&_pre]:p-3 [&_pre]:rounded-lg [&_pre]:mb-3 [&_pre]:overflow-x-auto
                          [&_pre_code]:bg-transparent [&_pre_code]:px-0
                          [&_blockquote]:border-l-4 [&_blockquote]:border-brand [&_blockquote]:pl-4 [&_blockquote]:text-fg-muted
                          [&_a]:text-brand [&_a]:underline
                          [&_strong]:font-semibold
                          [&_table]:w-full [&_table]:mb-3 [&_table]:border-collapse
                          [&_th]:border [&_th]:border-line [&_th]:p-2 [&_th]:text-left
                          [&_td]:border [&_td]:border-line [&_td]:p-2"
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                      </div>
                    )
                  ) : (
                    <span className="whitespace-pre-wrap">{message.content}</span>
                  )}
                </div>
                )}

                {renderBlocks && (message.blocks?.length || message.mode) ? (
                  <div className="w-full">{renderBlocks(message)}</div>
                ) : null}

                {message.role === 'assistant' && !message.insufficientEvidence && !message.blocks && (
                  <div className="flex gap-2 ml-1">
                    <button className="font-label-sm text-label-sm bg-bg-app text-brand px-3 py-1 rounded-full border border-brand/20 hover:bg-brand/10 transition-colors flex items-center gap-1">
                      <span className="material-symbols-outlined text-[14px]">description</span>
                      {t('chat.viewSource', 'View Source')}
                    </button>
                  </div>
                )}

                {message.role === 'assistant' && message.id === lastMessageId && ctaSlot && (
                  <div className="ml-1">{ctaSlot}</div>
                )}
              </div>
            ))}

            {/* Loading / Thinking State */}
            {loading && (
              <div className="flex flex-col items-start w-full gap-2 mt-4">
                <div className="relative overflow-hidden text-fg font-body-md text-body-md py-4 max-w-[85%] min-w-[200px]">
                  <div className="flex items-center gap-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-brand rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                      <div className="w-2 h-2 bg-brand rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 bg-brand rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                    <span className="text-fg-muted font-label-md text-label-md ml-2">{loadingLabel ?? t('chat.analyzing', 'Analyzing documents...')}</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </main>

      {/* Bottom Input Area (Fixed) */}
      <div ref={inputAreaRef} className={`absolute left-0 right-0 p-margin-mobile md:p-gutter transition-all duration-300 ${floatInput ? 'bottom-[20%]' : 'bottom-0 bg-gradient-to-t from-bg-app via-bg-app to-transparent pb-8'}`}>
        <div className="max-w-chat-width mx-auto relative group">
          {/* Glowing effect behind input */}
          <div className="absolute -inset-1 bg-gradient-to-r from-brand/20 to-brand/10 rounded-full blur opacity-25 group-focus-within:opacity-50 transition duration-500"></div>

          <div className="relative flex flex-col bg-bg-surface-2 rounded-3xl border border-line focus-within:border-brand/50 transition-colors shadow-lg">
            <div className="flex items-end px-4 py-3">
              <button className="p-2.5 text-fg-muted hover:text-fg transition-colors rounded-full shrink-0 self-start mt-0.5">
                <span className="material-symbols-outlined text-[24px]">add</span>
              </button>

              <textarea
                ref={textareaRef}
                className="flex-1 bg-transparent border-none focus:ring-0 text-fg font-body-lg text-body-lg placeholder-fg-muted resize-none px-2 py-3 min-h-[48px] max-h-[200px] outline-none"
                placeholder={t('chat.inputPlaceholder', 'Ask Cypremos AI')}
                rows={1}
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                onKeyDown={handleKeyPress}
                disabled={loading}
              />

              <div className="flex items-center gap-1 shrink-0 self-start mt-0.5">
                {!compact && (
                  <div className="hidden sm:flex items-center justify-center bg-bg-app rounded-full px-3 py-1.5 mr-2 border border-line">
                    <span className="text-xs text-fg-muted font-medium">Pro</span>
                    <span className="material-symbols-outlined text-[16px] text-fg-muted ml-1">expand_more</span>
                  </div>
                )}
                <button className="p-2.5 text-fg-muted hover:text-fg transition-colors rounded-full">
                  <span className="material-symbols-outlined text-[22px]">mic</span>
                </button>
                {inputValue.trim() && (
                  <button
                    onClick={onSend}
                    disabled={loading}
                    className="p-2.5 rounded-full transition-all bg-brand text-bg-surface hover:bg-brand/90 hover:scale-105 ml-1"
                  >
                    <span className="material-symbols-outlined text-[22px]" style={{ fontVariationSettings: "'FILL' 1" }}>arrow_upward</span>
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="text-center mt-3">
            <span className="font-label-sm text-xs text-fg-muted">{t('chat.disclaimer', 'AI can make mistakes. Verify important information.')}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
